import React, { useEffect } from 'react';
import { useParams } from 'react-router';

import styled from '@emotion/styled';

import StartsCard from '../../../components/Cards/StartsCard';
import DoughnutChart from '../../../components/Charts/DoughnutChart';
import useFeedbackAnalysisReport from '../../../hooks/dashboard/driver-list/useFeedbackAnalysisReport';

const FeedbackAnalysisChart = () => {
  const { driverId } = useParams();
  const { getFeedbackAnalysisReportData, feedbackAnalysisReportData } =
    useFeedbackAnalysisReport();

  useEffect(() => {
    getFeedbackAnalysisReportData(driverId ?? '');
    // eslint-disable-next-line
  }, []);

  const totalFeedbackGathered = feedbackAnalysisReportData?.totalCount ?? 0;
  const averageRatingScore = feedbackAnalysisReportData?.averageRating ?? 0;
  const averageRatingChange =
    feedbackAnalysisReportData?.averageRatingChange ?? 0;
  const totalCountChangePercent =
    feedbackAnalysisReportData?.totalCountChangePercent ?? 0;
  const positivePercentage =
    feedbackAnalysisReportData?.positivePercentage ?? 0;
  const negativeCount = feedbackAnalysisReportData?.negativeCount ?? 0;
  const positiveCount = feedbackAnalysisReportData?.positiveCount ?? 0;

  const incidentDistributionData = [
    {
      name: 'Positive Ratings',
      value: positiveCount,
      fill: '#4ADE80',
    },
    {
      name: 'Negative Ratings',
      value: negativeCount,
      fill: '#F87171',
    },
  ];

  // const totalReports = incidentDistributionData.reduce(
  //   (sum, item) => sum + item.value,
  //   0,
  // );

  return (
    <Container>
      <DoughnutBox>
        <DoughnutBoxSize>
          <DoughnutChart
            data={incidentDistributionData}
            showLegend={false}
            tooltipLabel='Rating Count'
            centerLabel='Positive'
            centerValue={positivePercentage}
            aspect={1}
            innerRadius='86%'
            outerRadius='98%'
          />
        </DoughnutBoxSize>
        <DoughnutBoxRightContent>
          <LegendList>
            {incidentDistributionData.map((item) => (
              <LegendItem key={item.name}>
                <LegendItemLeft>
                  <LegendSwatch color={item.fill} />
                  <LegendName>{item.name}</LegendName>
                </LegendItemLeft>
                <LegendValue color={item.fill}>{item.value}</LegendValue>
              </LegendItem>
            ))}
          </LegendList>
        </DoughnutBoxRightContent>
      </DoughnutBox>

      {/*  */}
      <RightBox>
        <StartsCard.Root>
          <StartsCard.Header>TOTAL FEEDBACK GATHERED</StartsCard.Header>
          <StartsCard.Value>{totalFeedbackGathered}</StartsCard.Value>
          <StartsCard.DynamicFooter trend={averageRatingChange}>
            {averageRatingChange >= 0 ? <span>↑</span> : <span>↓</span>}{' '}
            {averageRatingChange >= 0
              ? `+${averageRatingChange}%`
              : `-${averageRatingChange}%`}{' '}
            from last month
          </StartsCard.DynamicFooter>
        </StartsCard.Root>

        <StartsCard.Root>
          <StartsCard.Header>AVERAGE RATING SCORE</StartsCard.Header>
          <StartsCard.Value>
            {averageRatingScore.toFixed(1)}/5.0
          </StartsCard.Value>
          <StartsCard.DynamicFooter trend={totalCountChangePercent}>
            {totalCountChangePercent >= 0 ? <span>↑</span> : <span>↓</span>}{' '}
            {totalCountChangePercent >= 0
              ? `+${totalCountChangePercent}%`
              : `-${totalCountChangePercent}%`}{' '}
            from last month
          </StartsCard.DynamicFooter>
        </StartsCard.Root>
      </RightBox>
    </Container>
  );
};

export default FeedbackAnalysisChart;

const Container = styled.div`
  border-radius: 8px;
  display: flex;
  gap: 18px;
`;

const DoughnutBox = styled.div`
  display: grid;
  grid-template-columns: 169px 1fr;
  gap: 28px;
  padding-right: 14px;

  @media (max-width: 884px) {
    grid-template-columns: 1fr;
  }
`;

const DoughnutBoxSize = styled.div`
  width: 169px;
  height: 169px;
`;

const RightBox = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  /* border: 1px solid red; */
  width: 100%;
`;

const DoughnutBoxRightContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 40px;
`;

const LegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LegendItem = styled.div`
  display: grid;
  grid-template-columns: 140px auto;
  /* justify-content: space-between; */
  align-items: center;
  gap: 10px;
  width: fit-content;
`;

const LegendItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendSwatch = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ color }) => color};
`;

const LegendName = styled.div`
  color: #41415a;
  font-weight: 500;
  font-size: 14px;
`;

const LegendValue = styled.div<{ color: string }>`
  color: ${({ color }) => color};
  font-weight: 600;
  font-size: 14px;
`;
